import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import UnitModal from './UnitModal';
import TrainerAssignmentModal from './TrainerAssignmentModal';
import UnitDetailsModal from './UnitDetailsModal';
import ActionToast from '../../../components/ui/ActionToast';
import { hierarchyService } from '../../../services/hierarchyService';
import { unitService } from '../../../services/unitService';
import { useAuth } from '../../../contexts/AuthContext';
import assessmentService from '../../../services/assessmentService';
import MarksEntryModal from '../../formative-assessment-management/components/MarksEntryModal';

const UnitManagement = ({ program, initialType = 'all', onBack, trainerOnly = false, formativeOnly = false, hideHeader = false, titleBar = null }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedClassification, setSelectedClassification] = useState(initialType);
  const [selectedProgramId, setSelectedProgramId] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [periodScope, setPeriodScope] = useState(formativeOnly ? 'current' : 'all');
  const [departments, setDepartments] = useState([]);

  // Modal states
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showTrainerModal, setShowTrainerModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [viewingUnit, setViewingUnit] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [marksTimeline, setMarksTimeline] = useState(null);

  // Data states
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    loadInitialData();
    // Update classification if prop changes
    if (initialType) setSelectedClassification(initialType);
  }, [initialType, program, periodScope, formativeOnly]); // Refetch/Re-filter if program or period scope changes

  const fetchUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      const unitsData = await unitService?.getAll({ trainerOnly, currentOnly: trainerOnly && (!formativeOnly || periodScope === 'current') });
      setUnits(unitsData || []);
    } catch (err) {
      console.error('Error fetching units:', err);
      setError(err?.message || 'Failed to fetch units');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [unitsData, departmentsData] = await Promise.all([
        unitService?.getAll({ trainerOnly, currentOnly: trainerOnly && (!formativeOnly || periodScope === 'current') }),
        hierarchyService?.getDepartments()
      ]);
      setUnits(unitsData || []);
      setDepartments(trainerOnly
        ? [...new Map((unitsData || []).filter(unit=>unit?.program?.department?.id).map(unit=>[unit.program.department.id,unit.program.department])).values()]
        : (departmentsData || []));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterMarks = (unit) => {
    try {
      setNotice(null);
      setMarksTimeline({
        unit,
        title: 'CAM',
        total_marks: 100
      });
      setShowMarksModal(true);
    } catch (err) {
      console.error('Error opening marks entry:', err);
      setNotice({ type: 'error', message: 'Failed to open marks entry' });
    }
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'Core':
        return 'bg-primary text-primary-foreground';
      case 'Common':
        return 'bg-accent text-accent-foreground';
      case 'Basic':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getApprovalColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success text-success-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const filteredUnits = units?.filter(unit => {
    const matchesSearch = unit?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      unit?.code?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    // Filter by Program if provided prop exists
    const matchesProgram = program
      ? unit?.program?.id === program.id
      : selectedProgramId === 'all' || unit?.program?.id === selectedProgramId;

    // Filter by Department (ui filter)
    const matchesDepartment = selectedDepartment === 'all' ||
      unit?.program?.department?.id === selectedDepartment;

    // Filter by Classification (ui filter, synced with props)
    const matchesClassification = selectedClassification === 'all' ||
      String(unit?.unitType || unit?.type || '').toLowerCase() === String(selectedClassification).toLowerCase();

    const matchesLevel = selectedLevel === 'all' || String(unit?.programLevel || '') === selectedLevel;
    const matchesModule = selectedModule === 'all' || String(unit?.module || '') === selectedModule;

    return matchesSearch && matchesProgram && matchesDepartment && matchesClassification && matchesLevel && matchesModule;
  });

  const programmes = [...new Map(units.filter(unit => unit?.program?.id).map(unit => [unit.program.id, unit.program])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const availableLevels = [...new Set(units.map(unit => Number(unit?.programLevel)).filter(Boolean))].sort((a, b) => a - b);
  const availableModules = [...new Set(units.map(unit => Number(unit?.module)).filter(Boolean))].sort((a, b) => a - b);
  const hasActiveFilters = Boolean(searchTerm || selectedDepartment !== 'all' || selectedProgramId !== 'all' || selectedLevel !== 'all' || selectedModule !== 'all' || selectedClassification !== 'all' || (formativeOnly && periodScope !== 'current'));
  const resetFilters = () => {
    setSearchTerm(''); setSelectedDepartment('all'); setSelectedProgramId('all');
    setSelectedLevel('all'); setSelectedModule('all'); setSelectedClassification(initialType || 'all');
    if (formativeOnly) setPeriodScope('current');
  };

  const handleCreateUnit = () => {
    setSelectedUnit(null);
    setShowUnitModal(true);
  };

  const handleEditUnit = (unit) => {
    setViewingUnit(null);
    setSelectedUnit(unit);
    setShowUnitModal(true);
  };

  const handleSaveUnit = async (unitData) => {
    try {
      const pairIds = Array.isArray(unitData.pairedUnitIds) ? unitData.pairedUnitIds : [];
      const originalPartners = selectedUnit?.teachingGroupCode
        ? units.filter(item => item.id !== selectedUnit.id && item.teachingGroupCode === selectedUnit.teachingGroupCode)
        : [];
      const originalPartnerIds = originalPartners.map(item => item.id);
      const newlyAddedPairIds = pairIds.filter(id => !originalPartnerIds.includes(id));
      const removedPartnerIds = unitData.allowCombinedDelivery
        ? originalPartnerIds.filter(id => !pairIds.includes(id))
        : originalPartnerIds.length > 0 ? originalPartnerIds : [];
      const touchedIds = [...new Set([...newlyAddedPairIds, ...removedPartnerIds])];
      const pairingSnapshots = touchedIds.map(id => { const item = units.find(candidate => candidate.id === id); return { id, enabled: Boolean(item?.allowCombinedDelivery), code: item?.teachingGroupCode || null }; });
      const applyPairingChanges = async () => {
        for (const id of newlyAddedPairIds) await unitService.setUnitPairing(id, unitData.teachingGroupCode, true);
        for (const id of removedPartnerIds) await unitService.setUnitPairing(id, null, false);
        if (unitData.allowCombinedDelivery && newlyAddedPairIds.length > 0) {
          await unitService.synchronizeTeachingGroup(unitData.teachingGroupCode, newlyAddedPairIds[0]);
        }
      };
      const restorePairings = async () => {
        for (const snapshot of pairingSnapshots) await unitService.setUnitPairing(snapshot.id, snapshot.code, snapshot.enabled && Boolean(snapshot.code));
      };

      if (unitData?.isBulk) {
        // Refresh list after bulk upload
        await fetchUnits();
      } else if (selectedUnit?.id) {
        // Update existing unit
        await unitService?.update(selectedUnit?.id, unitData);
        if (newlyAddedPairIds.length > 0 || removedPartnerIds.length > 0) {
          try {
            await applyPairingChanges();
          } catch (pairError) {
            await restorePairings();
            await unitService.update(selectedUnit.id, selectedUnit);
            throw new Error(`The unit was not grouped: ${pairError.message}`);
          }
        }
        await fetchUnits();
      } else {
        // Create new unit
        const created = await unitService?.create(unitData);
        if (pairIds.length > 0) {
          try {
            await applyPairingChanges();
          } catch (pairError) {
            await restorePairings();
            if (created?.unit?.id) await unitService.delete(created.unit.id);
            throw new Error(`The unit was not created because pairing failed: ${pairError.message}`);
          }
        }
        await fetchUnits();
      }
      setShowUnitModal(false);
      setSelectedUnit(null);
      setNotice({ type: 'success', message: unitData?.isBulk ? 'Units were uploaded successfully.' : selectedUnit?.id ? `${unitData.code} was updated successfully.` : `${unitData.code} was created successfully.` });
    } catch (err) {
      console.error('Error saving unit:', err);
      setNotice({ type: 'error', message: err?.message || 'The unit could not be saved.' });
      throw err;
    }
  };

  const handleDeleteClick = (unit) => {
    setUnitToDelete(unit);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!unitToDelete) return;

    try {
      await unitService?.delete(unitToDelete?.id);
      await fetchUnits();
      setShowDeleteConfirm(false);
      setUnitToDelete(null);
      setNotice({ type: 'success', message: 'The unit was deleted successfully.' });
    } catch (err) {
      console.error('Error deleting unit:', err);
      setNotice({ type: 'error', message: err?.message || 'Failed to delete unit.' });
    }
  };

  const handleAssignTrainer = (unit) => {
    setSelectedUnit(unit);
    setShowTrainerModal(true);
  };

  const handleSaveTrainerAssignment = async (assignmentData) => {
    try {
      if (assignmentData?.unassign) {
         fetchUnits();
         return;
      }

      let result;
      if (selectedUnit?.trainerAssignment?.id) {
        // Update existing assignment
        result = await unitService?.updateTrainerAssignment(
          selectedUnit?.trainerAssignment?.id,
          { trainerId: assignmentData?.trainerId, unitId: selectedUnit.id }
        );
      } else {
        // Create new assignment
        result = await unitService?.assignTrainer(
          assignmentData?.unitId,
          assignmentData?.trainerId
        );
      }
      if (selectedUnit?.allowCombinedDelivery && selectedUnit?.teachingGroupCode) {
        await unitService.synchronizeTeachingGroup(selectedUnit.teachingGroupCode, selectedUnit.id);
      }
      await fetchUnits();
      setShowTrainerModal(false);
      setSelectedUnit(null);
      setNotice({
        type: 'success',
        message: result?.data?.requires_approval
          ? 'Trainer assignment was sent to the trainer’s mother-department HOD for approval.'
          : selectedUnit?.allowCombinedDelivery
            ? 'Trainer assigned and inherited by every unit in the combined class.'
            : 'Trainer assignment was saved successfully.'
      });
    } catch (err) {
      console.error('Error saving trainer assignment:', err);
      setNotice({ type: 'error', message: err?.message || 'Trainer assignment could not be saved.' });
      throw err;
    }
  };

  const tableHeaderClass = formativeOnly
    ? 'px-3 py-2 text-left text-xs font-semibold border-b border-slate-200'
    : 'px-4 py-3 text-left font-medium border-b-2 border-blue-100';
  const tableCellClass = formativeOnly
    ? 'px-3 py-2 border-b border-slate-100'
    : 'px-4 py-3 border-b border-blue-100';

  return (
    <div className={formativeOnly ? 'space-y-3' : 'space-y-6'}>
      <ActionToast notice={notice} onClose={() => setNotice(null)} />
      {/* Single-line Minimalistic Filter & Control Bar */}
      <div className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm flex flex-wrap lg:flex-nowrap items-center justify-between gap-3">
        {titleBar && (
          <div className="flex items-center gap-2 shrink-0">
            {titleBar}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
          <div className="w-40 min-w-[140px]">
            <Input type="search" placeholder="Search units..." value={searchTerm} onChange={(e) => setSearchTerm(e?.target?.value)} className="h-8 text-xs bg-white" />
          </div>
          {!program && (
            <select aria-label="Programme filter" value={selectedProgramId} onChange={e => setSelectedProgramId(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 max-w-[140px] focus:ring-2 focus:ring-primary focus:outline-none">
              <option value="all">All programmes</option>
              {programmes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          )}
          <select aria-label="Department filter" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e?.target?.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 max-w-[130px] focus:ring-2 focus:ring-primary focus:outline-none">
            <option value="all">All Depts</option>
            {departments?.map(dept => <option key={dept?.id} value={dept?.id}>{dept?.name}</option>)}
          </select>
          <select aria-label="Level filter" value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none">
            <option value="all">All levels</option>
            {availableLevels.map(level => <option key={level} value={String(level)}>Level {level}</option>)}
          </select>
          <select aria-label="Module filter" value={selectedModule} onChange={e => setSelectedModule(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none">
            <option value="all">All modules</option>
            {availableModules.map(module => <option key={module} value={String(module)}>Module {module}</option>)}
          </select>
          {formativeOnly && (
            <select aria-label="Period filter" value={periodScope} onChange={e => setPeriodScope(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none">
              <option value="current">Current semester</option>
              <option value="all">All semesters</option>
            </select>
          )}
          <select aria-label="Unit type filter" value={selectedClassification} onChange={e => setSelectedClassification(e.target.value)} className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-primary focus:outline-none">
            <option value="all">All unit types</option>
            <option value="Core">Core</option>
            <option value="Common">Common</option>
            <option value="Basic">Basic</option>
          </select>
          {hasActiveFilters && (
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={resetFilters}>
              <Icon name="RotateCcw" size={12} className="mr-1" />Reset
            </Button>
          )}

          <span className="text-xs text-slate-500 font-mono shrink-0">
            <strong>{filteredUnits.length}</strong> / {units.length} units
          </span>

          {!trainerOnly && (
            <Button variant="default" size="sm" className="h-8 px-3 text-xs font-semibold shrink-0" onClick={handleCreateUnit}>
              <Icon name="Plus" size={14} className="mr-1" />
              Add Unit
            </Button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Loading units...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-error/10 border border-error rounded-lg p-4 text-center">
          <Icon name="AlertCircle" size={24} className="mx-auto text-error mb-2" />
          <p className="text-error">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchUnits} className="mt-3">
            <Icon name="RefreshCw" size={14} className="mr-1" />
            Retry
          </Button>
        </div>
      )}

      {/* Units List */}
      {!loading && !error && (
        <div className={formativeOnly ? 'flex-1 rounded-lg border border-slate-200 overflow-hidden flex flex-col bg-background shadow-sm h-[calc(100vh-250px)]' : 'flex-1 rounded-md border border-blue-200 overflow-hidden flex flex-col bg-background shadow-sm h-[calc(100vh-280px)]'}>
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead className="bg-white text-muted-foreground sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className={tableHeaderClass}>Unit Code</th>
                  <th className={tableHeaderClass}>Name</th>
                  {!user?.homeDepartmentId && <th className={tableHeaderClass}>Department</th>}
                  <th className={tableHeaderClass}>Type</th>
                  {!formativeOnly && <th className={tableHeaderClass}>Credits</th>}
                  {!formativeOnly && <th className={tableHeaderClass}>Teaching Hrs/Wk</th>}
                  {!formativeOnly && <th className={tableHeaderClass}>Theory Exam Hrs</th>}
                  {!formativeOnly && <th className={tableHeaderClass}>Practical Exam Hrs</th>}
                  <th className={tableHeaderClass}>Enrolled</th>
                  {!formativeOnly && <th className={tableHeaderClass}>Trainer</th>}
                  <th className={`${tableHeaderClass} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {filteredUnits?.map((unit) => (
                  <tr key={unit?.id} className="hover:bg-blue-50/50 transition-colors group">
                    <td className={`${tableCellClass} font-mono text-xs text-blue-800`}>{unit?.code}</td>
                    <td className={`${tableCellClass} font-medium text-gray-800`}>{unit?.name}</td>
                    {!user?.homeDepartmentId && <td className={`${tableCellClass} text-gray-600`}>{unit?.program?.department?.name}</td>}
                    <td className={tableCellClass}>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {unit?.unitType || 'Core'}
                      </span>
                    </td>
                    {!formativeOnly && <td className={`${tableCellClass} text-gray-700`}>{unit?.creditHours}</td>}
                    {!formativeOnly && <td className={`${tableCellClass} text-gray-700`}>{(unit?.hoursPerWeek !== null && unit?.hoursPerWeek !== undefined) ? unit.hoursPerWeek : '-'}</td>}
                    {!formativeOnly && <td className={`${tableCellClass} text-gray-700`}>{(unit?.examTheoryHours !== null && unit?.examTheoryHours !== undefined) ? unit.examTheoryHours : '-'}</td>}
                    {!formativeOnly && <td className={`${tableCellClass} text-gray-700`}>{(unit?.examPracticalHours !== null && unit?.examPracticalHours !== undefined) ? unit.examPracticalHours : '-'}</td>}
                    <td className={`${tableCellClass} font-medium text-blue-600`}>{unit?.enrolled || 0}</td>
                    {!formativeOnly && <td className={`${tableCellClass} text-gray-700`}>
                      {unit?.trainerAssignment?.trainer ? (
                        <div className="flex flex-col">
                          <span className="text-sm">{unit?.trainerAssignment?.trainer?.fullName}</span>
                          <span className="text-xs text-gray-500">{unit?.trainerAssignment?.trainer?.department?.name}</span>
                        </div>
                      ) : (
                        String(unit?.trainerAssignment?.status || '').toLowerCase() === 'pending'
                          ? <span className="text-xs font-medium text-amber-600">Pending HOD approval</span>
                          : <span className="text-xs text-red-400 italic">Unassigned</span>
                      )}
                    </td>}
                    <td className={`${tableCellClass} text-right`}>
                      <div className={`flex items-center justify-end gap-2 transition-opacity ${formativeOnly ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewingUnit(unit)}
                          title="View Unit"
                        >
                          <Icon name="Eye" size={14} className="text-blue-600" />
                        </Button>
                        {trainerOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEnterMarks(unit);
                            }}
                            title={formativeOnly ? 'Formative entry' : 'Enter Marks'}
                            className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 flex gap-2 items-center"
                          >
                            <Icon name="ClipboardList" size={14} />
                            <span className="text-xs font-medium">{formativeOnly ? 'Formative entry' : 'Enter Marks'}</span>
                          </Button>
                        )}
                        {!trainerOnly&&<Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleAssignTrainer(unit)}
                          title={unit?.trainerAssignment ? 'Reassign Trainer' : 'Assign Trainer'}
                        >
                          <Icon name="UserPlus" size={14} className="text-muted-foreground hover:text-foreground" />
                        </Button>}
                        {!trainerOnly&&<Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditUnit(unit)}
                          title="Edit Unit"
                        >
                          <Icon name="Edit" size={14} className="text-muted-foreground hover:text-foreground" />
                        </Button>}
                        {!trainerOnly&&<Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(unit)}
                          title="Delete"
                          className="hover:bg-red-50"
                        >
                          <Icon name="Trash2" size={14} className="text-red-500" />
                        </Button>}
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredUnits?.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center text-muted-foreground">
                      <Icon name="Search" size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No units found matching your search</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <UnitModal
        isOpen={showUnitModal}
        onClose={() => {
          setShowUnitModal(false);
          setSelectedUnit(null);
        }}
        onSave={handleSaveUnit}
        unit={selectedUnit}
      />

      <TrainerAssignmentModal
        isOpen={showTrainerModal}
        onClose={() => {
          setShowTrainerModal(false);
          setSelectedUnit(null);
        }}
        onSave={handleSaveTrainerAssignment}
        unit={selectedUnit}
      />

      <UnitDetailsModal unit={viewingUnit} onClose={() => setViewingUnit(null)} onEdit={handleEditUnit} />

      {showMarksModal && (
        <MarksEntryModal
          isOpen={showMarksModal}
          onClose={() => {
            setShowMarksModal(false);
            setMarksTimeline(null);
          }}
          timeline={marksTimeline}
          onSuccess={() => {
            setNotice({ type: 'success', message: 'Marks updated successfully' });
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground mb-2">Delete Unit</h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete <strong>{unitToDelete?.code} - {unitToDelete?.name}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDeleteConfirm(false);
                  setUnitToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Unit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitManagement;
